import { SADropdownOption } from '@missionlabs/smartagent-app-components/dist/Dropdown';

import { PhoneNumberFormat } from 'google-libphonenumber';

import { IQueue, SmartAgentUser } from 'store/settings/settings.state';

import { timeFromDate, formatPhoneNumber, parseStringDate, wordToUpperCamelCase } from 'utils';

import { IInteraction } from 'views/Metrics/HistoricMetrics/types';
import {
    IContactSearchForm,
    ContactInitiationMethod,
    ChannelColumnGroup,
    ContactChannel,
    SocialContactType,
    ChatContactType,
    EmailContactType,
} from './contactSearch.types';
import { chatChannels, socialChannels, taskChannels, voiceChannels } from './contactSearch.constants';

const todayDate = new Date();
const yesterdayDate = new Date(todayDate);
yesterdayDate.setDate(yesterdayDate.getDate() - 1);

export const createDefaultContactSearchForm = (): IContactSearchForm => ({
    contactID: '',
    agent: undefined,
    queue: undefined,
    initiationMethod: undefined,
    externalID: '',
    phoneNumber: '',
    startDate: yesterdayDate,
    startTime: timeFromDate(yesterdayDate),
    endDate: todayDate,
    endTime: timeFromDate(todayDate),
    callDurationStartTime: '',
    callDurationEndTime: '',
    holdDurationStartTime: '',
    holdDurationEndTime: '',
    channel: {
        label: wordToUpperCamelCase(ContactChannel.ALL),
        data: ContactChannel.ALL,
        group: ChannelColumnGroup.ALL,
    },
    socialHandle: '',
    uniqueSocialId: '',
    chatEmail: '',
    chatOrderNumber: '',
    email: '',
    emailSearchType: { label: '', data: '' },
});

export const getContactSearchQueryString = (form: IContactSearchForm) =>
    new URLSearchParams(
        Object.fromEntries(
            Object.entries({
                phonenumberE164: formatPhoneNumber(form.phoneNumber, PhoneNumberFormat.E164),
                fromDate: form.startDate ? parseStringDate(form.startDate, form.startTime) : undefined,
                toDate: form.endDate ? parseStringDate(form.endDate, form.endTime, true) : undefined,
                initiationMethod: form.initiationMethod?.data,
                channel: form.channel?.data,
                agentID: form.agent?.ID,
                queueID: form.queue?.ID,
                contactID: form.contactID,
                externalID: form.externalID,
                socialHandle: form.socialHandle,
                uniqueSocialId: form.uniqueSocialId,
                chatEmail: form.chatEmail ? form.chatEmail.toLowerCase() : undefined,
                chatOrderNumber: form.chatOrderNumber,
                keyword: form.keyword ? form.keyword.toLowerCase() : undefined,
                taskEmail: form.email ? form.email.toLowerCase() : undefined,
                emailSearchType: form.emailSearchType.data,
            }).filter(([, v]) => !!v)
        ) as Record<string, string>
    ).toString();

const blankOption = { data: undefined, label: '' };

export const optionsFactory: {
    agent: (agents: SmartAgentUser[]) => SADropdownOption<SmartAgentUser | undefined>[];
    queue: (queues: IQueue[]) => SADropdownOption<IQueue | undefined>[];
    initiation: () => SADropdownOption<string | undefined>[];
    channel: () => SADropdownOption<string | undefined>[];
    socialSearchType: () => SADropdownOption<string | undefined>[];
    chatSearchType: () => SADropdownOption<string | undefined>[];
    emailSearchType: () => SADropdownOption<string>[];
} = {
    agent: agents => [
        blankOption,
        ...agents
            .filter(agent => !!agent.firstName)
            .map(agent => ({
                label: `${agent.firstName} ${agent.lastName}`,
                data: agent,
            }))
            .sort((a1, a2) => (a1.label.toLowerCase() > a2.label.toLowerCase() ? 1 : -1)),
    ],
    queue: queues => [
        blankOption,
        ...queues
            .map(q => ({ label: q.Name, data: q }))
            .sort((q1, q2) => (q1.label.toLowerCase() > q2.label.toLowerCase() ? 1 : -1)),
    ],
    initiation: () => [
        blankOption,
        ...Object.keys(ContactInitiationMethod).map(key => ({
            label: key,
            data: key,
        })),
    ],
    channel: () => [
        ...Object.keys(ContactChannel).map(key => {
            const formattedKey = wordToUpperCamelCase(key);
            return {
                label: formattedKey,
                data: key,
            };
        }),
    ],
    socialSearchType: () => [
        blankOption,
        ...Object.keys(SocialContactType).map(key => ({
            label: key,
            data: key,
        })),
    ],
    chatSearchType: () => [
        blankOption,
        ...Object.keys(ChatContactType).map(key => ({
            label: key,
            data: key,
        })),
    ],
    emailSearchType: () => [
        ...Object.keys(EmailContactType).map(key => ({
            label: key,
            data: EmailContactType[key as keyof typeof EmailContactType],
        })),
    ],
};

export const filterInteractionsByCallAndHoldDuration = (
    interactions: IInteraction[],
    searchParameters: IContactSearchForm
) => {
    const { callDurationStartTime, callDurationEndTime, holdDurationStartTime, holdDurationEndTime } = searchParameters;

    let filteredInteractions = interactions;

    const callDurationStartTimeParsed = parseTimeToSeconds(callDurationStartTime);
    if (callDurationStartTimeParsed !== undefined && !isNaN(callDurationStartTimeParsed)) {
        filteredInteractions = filteredInteractions.filter(
            interaction => interaction.agentInteractionDuration >= callDurationStartTimeParsed
        );
    }

    const callDurationEndTimeParsed = parseTimeToSeconds(callDurationEndTime);
    if (callDurationEndTimeParsed !== undefined && !isNaN(callDurationEndTimeParsed)) {
        filteredInteractions = filteredInteractions.filter(
            interaction => interaction.agentInteractionDuration <= callDurationEndTimeParsed
        );
    }

    const holdDurationStartTimeParsed = parseTimeToSeconds(holdDurationStartTime);
    if (holdDurationStartTimeParsed !== undefined && !isNaN(holdDurationStartTimeParsed)) {
        filteredInteractions = filteredInteractions.filter(
            interaction => interaction.holdDuration >= holdDurationStartTimeParsed
        );
    }

    const holdDurationEndTimeParsed = parseTimeToSeconds(holdDurationEndTime);
    if (holdDurationEndTimeParsed !== undefined && !isNaN(holdDurationEndTimeParsed)) {
        filteredInteractions = filteredInteractions.filter(
            interaction => interaction.holdDuration <= holdDurationEndTimeParsed
        );
    }

    return filteredInteractions;
};

const parseTimeToSeconds = (time: string) => {
    // Converts time in hh:mm:ss or hh:mm format to seconds
    if (!time) return undefined;

    const timeSplit = time.split(':');
    if (timeSplit.length === 3)
        return parseInt(timeSplit[0], 10) * 60 * 60 + parseInt(timeSplit[1], 10) * 60 + parseInt(timeSplit[2], 10);

    if (timeSplit.length === 2) return parseInt(timeSplit[0], 10) * 60 * 60 + parseInt(timeSplit[1], 10) * 60;

    return undefined;
};

export const getChannelGroup = (channel: string): ChannelColumnGroup => {
    if (socialChannels.includes(channel)) return ChannelColumnGroup.SOCIAL;
    if (voiceChannels.includes(channel)) return ChannelColumnGroup.VOICE;
    if (chatChannels.includes(channel)) return ChannelColumnGroup.CHAT;
    if (taskChannels.includes(channel)) return ChannelColumnGroup.TASK;

    return ChannelColumnGroup.ALL;
};
